<!--
  Guest login modal.
  - Desktop: existing UiModal (variant="default").
  - Mobile: fullscreen overlay matching the burger menu — same close button style
    (dark, right, in a separator bar) and deliberately NO body scroll lock.
  Content (GuestLoginForm) is shared between both variants.
  On success (email/password or Google) closes and navigates to /guest.
-->
<template>
  <!-- Desktop: standard UiModal -->
  <UiModal
    v-if="isDesktop"
    :modal-id="modalId"
    :open="props.open"
    variant="default"
    width="md:w-[30rem]"
    :close-with-history-back="openSource !== 'deep_link'"
    @hidden="emit('hidden')"
  >
    <template #head><UiLanguagePlaceholder domain="GuestLoginModal" name="title" /></template>
    <!-- TR-40866: after login/registration the newsletter question replaces
         the form; only the answer continues to /guest.
         TR-40769: also require props.open — UiModal stays mounted on desktop
         (renderIfHidden), and the Google button loads GSI immediately. -->
    <GuestNewsletterPrompt v-if="newsletterStep" source="modal" @answered="onNewsletterAnswered" />
    <GuestLoginForm v-else-if="props.open" :key="formKey" @success="onLoginSuccess" />
  </UiModal>

  <!-- Mobile: same frame as burger menu (dark X right, no scroll lock) -->
  <teleport to="body">
    <div
      v-if="!isDesktop && props.open"
      class="fixed inset-0 z-[1002] w-full h-full bg-white overflow-auto"
    >
      <div class="flex flex-row-reverse border-b border-neutral-200 py-2">
        <button class="py-2 pl-0 pr-3 cursor-pointer" title="close" @click="emit('hidden')">
          <UiIcon src="/svg/icons/cancel.svg" width="34" height="34" class="fill-neutral-800" />
        </button>
      </div>
      <div class="px-6 py-8">
        <div class="text-lg font-bold text-center mb-6"><UiLanguagePlaceholder domain="GuestLoginModal" name="title" /></div>
        <GuestNewsletterPrompt v-if="newsletterStep" source="modal" @answered="onNewsletterAnswered" />
        <GuestLoginForm v-else :key="formKey" @success="onLoginSuccess" />
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{(e: 'hidden'): void}>()

const modalId = useComponentId('guest-login')
const { log } = useGuestActivityLog()
const { initialMode, openSource } = useGuestLoginModal()
const { snoozeForOneDay } = useNewsletterPromptSnooze()
const guestStore = useGuestStore()
let completedLogin = false

// TR-40866: newsletter question inside the modal after a successful
// login/registration, before the redirect to /guest.
const newsletterStep = ref(false)

// Banner deep-link: UiModal's default close() is hide() + history.back().
// That restores `?guest=register` and the query watch reopens the modal.
// Close only hides; `guest` is already stripped from the current URL.

// Remount the form on every open: UiModal keeps hidden content mounted
// (renderIfHidden), so on desktop the form would otherwise reopen in its old
// state (e.g. stale code step), while mobile resets — this makes both reset.
const formKey = ref(0)
watch(() => props.open, (open) => {
  if (open) {
    formKey.value++
    completedLogin = false
    newsletterStep.value = false
    log('modal_open', { source: openSource.value, tab: initialMode.value })
  } else {
    // Closing the modal (X / backdrop) during the newsletter step hides the
    // prompt for one day — it is not a permanent "Jetzt nicht".
    if (newsletterStep.value) {
      newsletterStep.value = false
      snoozeForOneDay()
      guestStore.clearNewsletterPromptPending()
      log('newsletter_prompt_hidden_session', { source: 'modal' })
    }
    log(completedLogin ? 'modal_close' : 'modal_abandon')
  }
})

// Breakpoint switch: desktop uses UiModal, mobile uses burger-menu overlay.
// SSR default = desktop (prevents hydration mismatch), evaluated client-side.
// md = 768px matches the Tailwind breakpoint UiModal uses for "desktop".
const isDesktop = ref(true)
if (import.meta.client) {
  const mq = window.matchMedia('(min-width: 768px)')
  const update = () => { isDesktop.value = mq.matches }
  onMounted(() => {
    update()
    mq.addEventListener('change', update)
  })
  onBeforeUnmount(() => mq.removeEventListener('change', update))
}

const onLoginSuccess = () => {
  completedLogin = true
  // TR-40866: pending newsletter prompt -> ask inside the modal first, the
  // redirect to /guest happens only after the question is answered.
  if (guestStore.user?.newsletter_prompt_pending && !newsletterStep.value) {
    newsletterStep.value = true
    log('newsletter_prompt_shown', { source: 'modal' })
    return
  }
  finishLogin()
}

const finishLogin = () => {
  emit('hidden')
  // Already inside the guest area (login prompt on /guest*): stay on the
  // current page, the layout switches reactively to the page content.
  if (!useRoute().path.startsWith('/guest')) {
    navigateTo('/guest')
  }
}

const onNewsletterAnswered = () => {
  // Reset the step BEFORE closing: the props.open watcher otherwise treats
  // the close as an unanswered prompt and double-dismisses.
  newsletterStep.value = false
  finishLogin()
}

// Google button success flows through the central GIS callback via the
// googleLoginSuccessAt signal. Only navigate when THIS modal is open — prevents
// One Tap from triggering navigation when the modal is closed.
const { googleLoginSuccessAt } = useGoogleIdentity()
watch(googleLoginSuccessAt, () => {
  if (props.open) {
    onLoginSuccess()
  }
})
</script>
